import { PrintUnitType } from '../types/enums';
import { PrintUnit } from '../types/printUnit';

/**
 * 앨범명/가수명을 인쇄부 content 문자열로 포맷.
 * - 둘 다 있으면 "제목 - 가수"
 * - 하나만 있으면 그 값
 * - 둘 다 없으면 빈 문자열
 */
export function formatAlbumContent(
  title?: string | null,
  artist?: string | null,
): string {
  const t = (title ?? '').trim();
  const a = (artist ?? '').trim();
  if (t && a) return `${t} - ${a}`;
  return t || a || '';
}

/**
 * 앨범 정보(제목/가수) 변경을 자동으로 반영하는 인쇄부 목록.
 * - 인쇄부 2 (측면)
 * - 인쇄부 3 (뒷면 상단)
 * - 인쇄부 6 (디스크 측면)
 */
export const ALBUM_CONTENT_SYNC_TARGETS: PrintUnitType[] = [
  PrintUnitType.SIDE,
  PrintUnitType.BACK_TOP,
  PrintUnitType.DISC_SIDE,
];

/**
 * content가 있는 인쇄부 유형을 판별 (타입 가드).
 * SIDE, BACK_TOP, DISC_SIDE, DISC2만 content 필드를 가짐.
 */
type UnitWithContent = Extract<PrintUnit, { content?: string }>;
function hasContentField(unit: PrintUnit): unit is UnitWithContent {
  return 'content' in unit;
}

/**
 * 앨범 정보 변경 시(setAlbumInfo) 인쇄부 content를 일괄 동기화.
 * - 제목/가수 중 하나라도 값이 있으면: 계산된 content로 덮어쓰고 useText=true
 * - 둘 다 비어있으면: 해당 인쇄부는 그대로 유지(기존 커스텀 content 보존)
 */
export function syncAlbumContentOnInfoChange(
  printUnits: Record<PrintUnitType, PrintUnit>,
  albumInfo: { title?: string; artist?: string },
): Record<PrintUnitType, PrintUnit> {
  const content = formatAlbumContent(albumInfo.title, albumInfo.artist);
  if (!content) return printUnits;

  const next = { ...printUnits };
  for (const key of ALBUM_CONTENT_SYNC_TARGETS) {
    const unit = next[key];
    if (unit && hasContentField(unit)) {
      next[key] = { ...unit, content, useText: true } as PrintUnit;
    }
  }
  return next;
}

/**
 * 인쇄부 선택 시(setSelectedPrintUnit) content 자동 채우기.
 * - 선택된 인쇄부가 동기화 대상이고, 기존 content가 비어있으며,
 *   앨범 정보가 하나라도 있으면: 계산된 content 주입, useText=true
 * - 그 외: 그대로 유지(기존 커스텀 content 보존)
 */
export function syncAlbumContentOnSelect(
  printUnits: Record<PrintUnitType, PrintUnit>,
  selectedType: PrintUnitType,
  albumInfo: { title?: string; artist?: string },
): Record<PrintUnitType, PrintUnit> {
  if (!ALBUM_CONTENT_SYNC_TARGETS.includes(selectedType)) return printUnits;

  const unit = printUnits[selectedType];
  if (!unit || !hasContentField(unit)) return printUnits;
  if (unit.content) return printUnits;

  const content = formatAlbumContent(albumInfo.title, albumInfo.artist);
  if (!content) return printUnits;

  return {
    ...printUnits,
    [selectedType]: { ...unit, content, useText: true } as PrintUnit,
  };
}
